/**
 * MiniAnalysisWorker - Worker för batch-analys med mini-modell
 * 
 * Ansvarig för:
 * - Processning av data-batchar från kön
 * - Anrop till callMiniJSON() med GPT-4.1-mini
 * - Strikt JSON-schema output per batch
 * - Retry-logik med exponential backoff
 */

import { supabase } from '@/integrations/supabase/client';
import { callMiniJSON } from '@/lib/azure-openai';
import { PremiumReportWorker } from './PremiumReportWorker';
import { deployments } from '@/lib/azure-openai/env';
import { BatchResult, BatchProcessingResult, WorkerJob } from './types';
import { safeEstimateTokens } from '@/lib/utils/tokens';
import { normalizeSwedishNumber, normalizeSwedishDate, transformDataForAnalysis, normalizeHeaderName } from '@/lib/csv-parser';
import { createTaxAnalysisPrompt, DEFAULT_COMPANY_CONFIG } from '@/lib/tax-calendar-helpers';
import { inferSchema } from '@/lib/csv-schema-sniffer';
import { calculateBatchStatistics, type StatisticalSummary } from '@/lib/statistical-analysis';

export class MiniAnalysisWorker {
  private workerId: string;
  private isRunning = false;
  private maxConcurrentJobs = 1; // Concurrency=1 tills 0 st 429 i loggarna
  private currentJobs = 0;
  private batchSleepMs = 900; // 900ms mellan batchar (från spec)
  private reportWorker: PremiumReportWorker;
  private verboseLogging = false; // Disable spam by default

  // Retry config: 429-fel (rate limiting) ska INTE räknas mot max_attempts
  // Endast riktiga fel (5xx, parsing) räknas
  private miniRetries = 5; // Max för riktiga fel
  private retryDelays = [60000, 120000, 300000, 600000, 600000]; // 1min, 2min, 5min, 10min, 10min
  
  // Separata inställningar för 429 rate-limit retries (obegränsade försök, längre backoff)
  private rateLimitMaxBackoffMs = 15 * 60 * 1000; // 15 minuter max backoff för 429
  private rateLimitRetries = 50; // Mycket högt - vi vill inte ge upp pga Azure-överbelastning
  
  // Token-bucket throttling för att undvika 429 (Azure: 100k TPM)
  // Läses dynamiskt från ai_deployments vid start
  private targetTPM = 60000; // Default fallback
  private targetRPM = 50; // Max 50 requests per minut
  private tokenWindow: Array<{ timestamp: number; tokens: number }> = [];
  private requestWindow: Array<{ timestamp: number }> = [];
  private deploymentName = deployments.mini; // Deployment att använda (synkad med env)


  // Statistics tracking
  public stats = {
    jobsProcessed: 0,
    errors: 0,
    averageProcessingTime: 0,
    lastJobAt: null as Date | null,
    currentJob: null as string | null
  };

  constructor(workerId: string) {
    this.workerId = workerId;
    this.reportWorker = new PremiumReportWorker(`report-${workerId}`);
  }

  /**
   * Startar worker-loopen med event-driven approach via Realtime
   */
  async start(): Promise<void> {
    if (this.isRunning) {
      console.log(`MiniAnalysisWorker ${this.workerId} är redan igång`);
      return;
    }

    this.isRunning = true;
    console.log(`🚀 MiniAnalysisWorker ${this.workerId} startar med event-driven model`);

    // Läs dynamisk TPM från ai_deployments
    await this.loadDeploymentConfig();

    // Återställ eventuella orphaned jobs från tidigare worker-crasher
    await this.cleanupOrphanedJobs();

    // Registrera worker
    await this.registerWorker();
    
    // P0-4: Event-driven workers via Supabase Realtime
    // Lyssna på INSERT events i worker_queue tabellen
    const channel = supabase.channel(`worker-queue-${this.workerId}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'worker_queue'
        },
        (payload) => {
          console.log(`🔔 [Realtime] Nytt jobb detekterat via Realtime:`, payload.new);
          
          // Trigga kick() för att omedelbart hämta och processa jobbet
          this.kick().catch(err => {
            console.error(`❌ Fel vid Realtime-triggad kick:`, err);
          });
        }
      )
      .subscribe(async (status) => {
        if (status === 'SUBSCRIBED') {
          console.log(`✅ Worker ${this.workerId} prenumererar på worker_queue INSERT events`);
          
          // Kicka igång omedelbart för att kolla efter befintliga jobb
          await this.kick().catch(err => {
            console.error(`❌ Fel vid initial kick:`, err);
          });
        } else if (status === 'CHANNEL_ERROR') {
          console.error(`❌ Worker ${this.workerId} Realtime channel error`);
        }
      });
    
    // Spara channel-referens för cleanup
    (this as any).realtimeChannel = channel;
    
    // Starta huvudloopen som fallback (polling)
    console.log(`🔄 Worker ${this.workerId} startar fallback polling-loop (900ms)`);
    this.processJobs();
  }

  /**
   * Stoppar worker-loopen och stänger Realtime-prenumeration
   */
  stop(): void {
    this.isRunning = false;
    
    // Stäng Realtime channel om den finns
    const channel = (this as any).realtimeChannel;
    if (channel) {
      console.log(`🔌 Worker ${this.workerId} stänger Realtime-prenumeration`);
      supabase.removeChannel(channel);
      (this as any).realtimeChannel = null;
    }
    
    console.log(`MiniAnalysisWorker ${this.workerId} stoppad`);
  }

  /**
   * Toggla verbose logging för debugging
   */
  public setVerboseLogging(enabled: boolean): void {
    this.verboseLogging = enabled;
    console.log(`🔊 Worker ${this.workerId} verbose logging: ${enabled ? 'ON' : 'OFF'}`);
  }

  public async kick(): Promise<void> {
    console.log(`🦶 Kick received for ${this.workerId}`);
    // If main loop isn't running yet, do a single attempt to fetch/process a job
    if (this.currentJobs >= this.maxConcurrentJobs) {
      return;
    }

    try {
      const job = await this.getNextJob();
      if (job) {
        this.stats.currentJob = job.job_id;
        this.processJobAsync(job);
      } else {
        console.log(`⏳ ${this.workerId} no job available on kick`);
      }
    } catch (e) {
      console.warn(`⚠️ ${this.workerId} kick attempt failed:`, e);
    }
  }

  /**
   * Processerar ett enskilt batch-jobb med heartbeat
   */
  async processBatchJob(jobId: string, batchData: any[], job?: any): Promise<BatchProcessingResult> {
    const startTime = Date.now();
    const batchId = job?.batch_id;
    
    // Starta heartbeat om vi har ett batch_id
    let heartbeatInterval: number | undefined;
    if (batchId) {
      heartbeatInterval = setInterval(async () => {
        try {
          await supabase
            .from('batches')
            .update({ updated_at: new Date().toISOString() })
            .eq('id', batchId);
          console.log(`💓 [Worker ${this.workerId}] Heartbeat sent for batch ${batchId}`);
        } catch (err) {
          console.warn(`⚠️ [Worker ${this.workerId}] Heartbeat failed:`, err);
        }
      }, 60000) as unknown as number; // Var 60:e sekund
    }
    
    try {
      // STEG 1: Verifiera payload-struktur och field_mapping
      const fieldMapping = (job as any)?.payload?.field_mapping || null;
      
      console.log(`🔍 [${jobId}] PAYLOAD VERIFICATION:`, {
        has_payload: !!(job as any)?.payload,
        has_field_mapping: !!fieldMapping,
        field_mapping: fieldMapping,
        payload_keys: Object.keys((job as any)?.payload || {})
      });

      // DEBUG: Logga batch-data för att se vad som faktiskt kommer in
      console.log(`🔍 [${jobId}] Batch data received:`, {
        isArray: Array.isArray(batchData),
        length: batchData?.length,
        type: typeof batchData,
        firstRow: batchData?.[0],
        sample: batchData?.slice(0, 2)
      });

      // Validera att vi faktiskt har data
      if (!batchData || !Array.isArray(batchData) || batchData.length === 0) {
        console.error(`❌ [${jobId}] TOM ELLER OGILTIG BATCH DATA:`, batchData);
        throw new Error('Batch data är tom eller ogiltig');
      }

      // STEG 5: Validera och sanitera fieldMapping
      const validatedMapping = this.validateFieldMapping(fieldMapping, jobId);
      if (!validatedMapping) {
        console.error(`❌ [${jobId}] KRITISKT: Saknad eller ofullständig fieldMapping. Kan inte fortsätta.`);
        throw new Error('Kritiska fält saknas i fieldMapping (Datum, Belopp, Händelse)');
      }

      // Estimera tokens för kostnadskontroll och telemetri
      const estimatedTokens = safeEstimateTokens(batchData);
      
      // Token-bucket throttling: vänta om vi närmar oss TPM-gränsen
      await this.throttleIfNeeded(estimatedTokens);
      
      const deployment = this.deploymentName; // använd samma deployment överallt
      console.log(`🧠 [${jobId}] Start | dep=${deployment} | estTokens=${estimatedTokens} | rows=${batchData.length} | timeout=60s | attempt=${this.stats.jobsProcessed + 1}`);

      // STEG 3: Förbered data för analys med validerad fältmappning OCH headers för deterministisk mappning
      const headers = (job as any)?.payload?.headers || null;
      console.log(`🔍 [${jobId}] Headers from payload:`, headers);
      
      const analysisInput = this.prepareAnalysisData(batchData, validatedMapping, headers);

      // NYTT: Beräkna statistik för batchen
      console.log(`📊 [${jobId}] Beräknar statistik för ${analysisInput.transactions.length} transaktioner...`);
      const statistics = calculateBatchStatistics(analysisInput.transactions);

      console.log(`📊 [${jobId}] ========== STATISTIK BERÄKNAD ==========`);
      console.log(`📊 [${jobId}] Antal skattetyper: ${statistics.tax_types.length}`);
      
      // Detaljerad loggning av ALLA skattetyper
      statistics.tax_types.forEach((taxType, idx) => {
        console.log(`📊 [${jobId}] Skattetyp ${idx + 1}:`, {
          namn: taxType.tax_type,
          antal: taxType.count,
          median: taxType.monthly_median,
          genomsnitt: taxType.monthly_average,
          stdDev: taxType.std_deviation,
          cv: taxType.coefficient_of_variation,
          min: taxType.min,
          max: taxType.max
        });
      });
      
      console.log(`📊 [${jobId}] Betalningsbeteende:`, {
        totalt: statistics.payment_behavior.total_payments,
        sena: statistics.payment_behavior.late_payments,
        i_tid: statistics.payment_behavior.on_time_payments,
        snitt_försening_dagar: statistics.payment_behavior.average_days_late,
        max_försening_dagar: statistics.payment_behavior.max_days_late,
        räntor: statistics.payment_behavior.interest_charges_count,
        avgifter: statistics.payment_behavior.fee_charges_count
      });
      console.log(`📊 [${jobId}] ==========================================`);

      // Lägg till statistik i analysis_context
      analysisInput.analysis_context.statistics = statistics;

      // Extrahera år från första giltiga transaktion
      let dataYear: number | undefined;
      if (analysisInput.transactions && analysisInput.transactions.length > 0) {
        const firstDate = analysisInput.transactions.find((t: any) => t.date)?.date;
        if (firstDate) {
          dataYear = new Date(firstDate).getFullYear();
          console.log(`📅 [${jobId}] Detekterat dataår: ${dataYear}`);
        }
      }

      // STEG 4: Skapa system prompt MED fieldMapping och statistik explicit inkluderad
      const companyConfig = (job as any)?.payload?.company_config || { companySize: 'small', vatReportingPeriod: 'monthly' };
      const systemPrompt = this.createAnalysisPromptWithStatistics(companyConfig, validatedMapping, statistics, dataYear);

      // 🔍 DEBUG LOGGING: Logga prompt och precomputed totals
      console.log(`📝 [${jobId}] ========== SYSTEM PROMPT ==========`);
      console.log(systemPrompt);
      console.log(`📝 [${jobId}] ===================================`);
      console.log(`📊 [${jobId}] ANALYSIS INPUT:`, {
        transaction_count: analysisInput.transactions?.length,
        first_transaction: analysisInput.transactions?.[0],
        precomputed_totals: analysisInput.analysis_context?.precomputed_totals,
        has_field_mapping: analysisInput.analysis_context?.has_field_mapping,
        sample_amounts: analysisInput.transactions?.slice(0, 5).map((t: any) => ({ 
          amount: t.amount, 
          direction: t.direction,
          raw_amount: t.raw_amount 
        }))
      });

      // Anropa mini-modell med retry (exponential backoff med jitter)
      let response;
      try {
        // Global rate limit gate via DB (delad mellan alla workers/instanser)
        try {
          const { data: allowed } = await supabase.rpc('check_rate_limit', {
            deployment_name: deployment,
            tokens_needed: Math.min(estimatedTokens, this.targetTPM)
          });
          if (allowed === false) {
            const now = Date.now();
            const msToNextMinute = ((60 - (Math.floor(now / 1000) % 60)) * 1000) + 2000; // +2s marginal
            console.warn(`🛑 [${jobId}] Global rate limit gate: väntar ${Math.round(msToNextMinute/1000)}s`);
            await this.sleep(msToNextMinute);
          }
        } catch (gateErr) {
          console.warn(`⚠️ [${jobId}] Rate limit gate check misslyckades, fortsätter defensivt:`, gateErr);
        }

        console.log(`🔄 [${jobId}] Anropar Azure OpenAI (load-locking hanteras i edge function)...`);
        
        response = await this.callWithRetry(async () => {
          return await callMiniJSON(
            [
              { role: 'system', content: systemPrompt },
              { role: 'user', content: JSON.stringify(analysisInput) }
            ],
            8000 // Revision needs more output tokens for comprehensive analysis
          );
        }, this.miniRetries);
        console.log(`✅ [${jobId}] Azure OpenAI svarade!`, {
          hasResult: !!response?.result,
          hasUsage: !!response?.usage,
          responseType: typeof response
        });

        // Kontrollera om AI:n nådde token-limit
        if (response.usage?.finish_reason === 'length') {
          console.error(`❌ [${jobId}] AI:n nådde token-limit (16K) trots maxgräns! Detta bör inte hända.`);
          console.error(`[${jobId}] Usage:`, response.usage);
          
          // Flagga för manuell granskning
          if (response.result) {
            (response.result as any).needs_human_review = true;
            (response.result as any).truncation_warning = 
              "AI-svaret avbröts pga token-limit. Kontakta support - detta indikerar extremt stort dataset.";
          }
        }
      } catch (error) {
        const errorMsg = error instanceof Error ? error.message : String(error);
        const isRateLimitError = errorMsg.toLowerCase().includes('rate limit') || 
                                  errorMsg.includes('429') ||
                                  errorMsg.toLowerCase().includes('too many requests');
        
        console.error(`❌ [${jobId}] Azure OpenAI CALL ERROR:`, {
          message: errorMsg,
          stack: error instanceof Error ? error.stack : undefined,
          errorType: error?.constructor?.name,
          isRateLimitError,
          timestamp: new Date().toISOString()
        });
        
        // Re-throw med flagga för rate limiting
        if (isRateLimitError) {
          const enhancedError = new Error(errorMsg);
          (enhancedError as any).isRateLimitError = true;
          throw enhancedError;
        }
        
        throw error; // Re-throw för att jobbet ska markeras som failed
      }
      
      // 🔍 DEBUG LOGGING: Logga AI-svar före fallback-korrigering
      const rawResult = response.result || response;
      console.log(`🤖 [${jobId}] ========== AI RESPONSE ==========`);
      console.log(`🤖 [${jobId}] RAW RESPONSE:`, JSON.stringify(rawResult, null, 2));
      console.log(`🤖 [${jobId}] Response structure:`, {
        has_result: !!response.result,
        has_metrics: !!(rawResult as any)?.metrics,
        raw_metrics: (rawResult as any)?.metrics,
        raw_flags_count: Array.isArray((rawResult as any)?.flags) ? (rawResult as any).flags.length : 0
      });

      // Extrahera result och usage
      const result = response.result || response;
      const usage = response.usage as { prompt_tokens?: number; completion_tokens?: number; total_tokens?: number } | undefined;

      // Telemetri: logga usage och latency
      const latencyMs = Date.now() - startTime;
      if (usage?.total_tokens) {
        console.log(`📊 [${jobId}] OK | tokens: in=${usage.prompt_tokens || 0}, out=${usage.completion_tokens || 0}, total=${usage.total_tokens} | latency=${latencyMs}ms`);
        // Uppdatera token-bucket
        this.recordTokenUsage(usage.total_tokens);
      } else {
        console.log(`📊 [${jobId}] OK | latency=${latencyMs}ms`);
      }

      // Validera och normalisera resultat
      const batchResult = this.validateBatchResult(result, jobId);
      
      // 🩹 Fallback-korrigering: om modellen returnerar 0 i belopp men vi har normaliserade totals
      try {
        const pre = (analysisInput as any)?.analysis_context?.precomputed_totals;
        // If model returned zero sums but we have normalized totals, apply them
        if (pre && (Number(batchResult.metrics.sum_in) === 0 && Number(batchResult.metrics.sum_out) === 0) && (pre.sum_in > 0 || pre.sum_out > 0)) {
          batchResult.metrics.sum_in = pre.sum_in;
          batchResult.metrics.sum_out = pre.sum_out;
          console.log(`🩹 [${jobId}] Applied fallback totals from normalized data: in=${pre.sum_in}, out=${pre.sum_out}`);
        }
        // Always ensure count reflects number of transactions if model returned 0
        if (pre && Number(batchResult.metrics.count) === 0 && Number(pre.count) > 0) {
          batchResult.metrics.count = pre.count;
          if (!batchResult.top_counterparties || batchResult.top_counterparties.length === 0) {
            batchResult.top_counterparties = ['Skatteverket'];
          }
          console.log(`🩹 [${jobId}] Applied fallback count from normalized data: count=${pre.count}`);
        }
      } catch (e) {
        console.warn(`⚠️ [${jobId}] Could not apply fallback corrections:`, e);
      }
      
      // Enforce a minimum number of informative low-risk flags per batch to avoid empty findings
      try {
        const txs: any[] = (analysisInput as any)?.transactions || [];
        if (Array.isArray(batchResult.flags) && batchResult.flags.length < 3 && txs.length > 0) {
          const needed = 3 - batchResult.flags.length;
          const infoFlags: any[] = [];

          // 1) Ränta/avgift i event-text
          for (let i = 0; i < txs.length && infoFlags.length < needed; i++) {
            const ev = String(txs[i]?.event || '').toLowerCase();
            if (ev.includes('ränta') || ev.includes('ranta') || ev.includes('avgift')) {
              infoFlags.push({
                id: `auto_info_interest_${i}`,
                rule: 'Informativt: ränta/avgift',
                score: 5,
                severity: 'low',
                description: `Transaktion markerad som ränta/avgift ${txs[i]?.date || '[okänt datum]'} på belopp ${txs[i]?.amount ?? '—'}`
              });
            }
          }

          // 2) Runda belopp (multiplar av 1000)
          for (let i = 0; i < txs.length && infoFlags.length < needed; i++) {
            const amt = Number(txs[i]?.amount);
            if (!isNaN(amt) && Math.abs(amt) > 0 && Math.abs(amt) % 1000 === 0) {
              infoFlags.push({
                id: `auto_info_round_${i}`,
                rule: 'Informativt: runt belopp',
                score: 3,
                severity: 'low',
                description: `Runt belopp observerat ${txs[i]?.date || '[okänt datum]'}: ${amt}`
              });
            }
          }

          // 3) Generiska informativa flaggor för resterande
          for (let i = 0; i < txs.length && infoFlags.length < needed; i++) {
            const amt = txs[i]?.amount;
            infoFlags.push({
              id: `auto_info_tx_${i}`,
              rule: 'Informativt: transaktion i perioden',
              score: 1,
              severity: 'low',
              description: `Transaktion den ${txs[i]?.date || '[okänt datum]'} med belopp ${amt ?? '—'}`
            });
          }

          if (infoFlags.length > 0) {
            batchResult.flags = [...batchResult.flags, ...infoFlags];
            console.log(`ℹ️ [${jobId}] Added ${infoFlags.length} informative flags to ensure minimum findings per batch`);
          }
        }
      } catch (e) {
        console.warn(`⚠️ [${jobId}] Could not enforce minimum informative flags:`, e);
      }

      const processingTime = Date.now() - startTime;
      console.log(`✅ [${jobId}] Complete | time=${processingTime}ms`);

      return {
        batchResult,
        tokensIn: usage?.prompt_tokens || 0,
        tokensOut: usage?.completion_tokens || 0
      };

    } catch (error) {
      // Rensa heartbeat vid fel
      if (heartbeatInterval) {
        clearInterval(heartbeatInterval);
      }
      
      const processingTime = Date.now() - startTime;
      const errorMsg = error instanceof Error ? error.message : String(error);
      const shortError = errorMsg.slice(0, 300);
      console.error(`❌ [${jobId}] FAIL | time=${processingTime}ms | error=${shortError}`);
      throw error;
    } finally {
      // Säkerställ att heartbeat alltid stoppas
      if (heartbeatInterval) {
        clearInterval(heartbeatInterval);
      }
    }
  }

  // === PRIVATE METHODS ===

  /**
   * Försöker hämta nästa jobb från kön med concurrency-kontroll
   * Prioriterar server-side reservation via worker-reserve-batch för atomicitet
   */
  private async getNextJob(): Promise<WorkerJob | null> {
    console.log(`[Worker ${this.workerId}] 🔍 getNextJob: Starting job fetch...`);

    // STEG 1: Försök server-side atomisk reservation via edge function
    console.log(`[Worker ${this.workerId}] 📡 Calling worker-reserve-batch edge function...`);
    try {
      const { data: reserveData, error: reserveError } = await supabase.functions.invoke('worker-reserve-batch', {
        body: {}
      });

      if (reserveError) {
        console.warn(`[Worker ${this.workerId}] ⚠️ worker-reserve-batch error:`, reserveError.message);
      } else if (reserveData?.batch) {
        const batch = reserveData.batch;
        console.log(`[Worker ${this.workerId}] ✅ Reserved batch via edge function: ${batch.batch_id}`);
        
        // Synka worker_queue för diagnostik
        await this.syncQueueForReservedBatch(batch);
        
        // Skapa fallback-jobb från reserverad batch
        return this.createFallbackJob(batch);
      } else {
        console.log(`[Worker ${this.workerId}] 📭 No batch available from worker-reserve-batch (${reserveData?.reason || 'unknown'})`);
      }
    } catch (edgeFunctionError) {
      console.warn(`[Worker ${this.workerId}] ⚠️ Edge function call failed:`, edgeFunctionError);
    }

    // STEG 2: Fallback till client-side SELECT och optimistic UPDATE
    console.log(`[Worker ${this.workerId}] 🔄 Falling back to client-side queue SELECT...`);
    const { data: jobs, error } = await supabase
      .from('worker_queue')
      .select('*')
      .in('queue_name', ['premium_report', 'batch_analysis'])
      .eq('status', 'pending')
      .or('retry_after.is.null,retry_after.lte.' + new Date().toISOString())
      .order('priority', { ascending: false })
      .order('created_at', { ascending: true })
      .limit(1);

    if (error || !jobs || jobs.length === 0) {
      console.log(`[Worker ${this.workerId}] 📭 No pending jobs in worker_queue`);
      
      // Extra nudge: Om kön fortfarande visar pending efter 3s, kicka workers igen
      setTimeout(async () => {
        const { data: stillPending } = await supabase
          .from('worker_queue')
          .select('id')
          .eq('status', 'pending')
          .limit(1);
        if (stillPending && stillPending.length > 0) {
          console.log(`[Worker ${this.workerId}] 🔔 Found pending jobs after timeout, triggering kick...`);
          // Använd en enkel "kick" genom att signalera en Realtime event (eller bara logga)
          // I praktiken kommer polling i TestlabRealDataPage att kicka workers
        }
      }, 3000);
      
      return null;
    }

    const job = jobs[0];
    console.log(`[Worker ${this.workerId}] 🎯 Found job in worker_queue: ${job.job_id}, attempting optimistic update...`);

    // Försök sätta status=processing atomiskt (race condition protection)
    const { error: updateError } = await supabase
      .from('worker_queue')
      .update({
        status: 'processing',
        assigned_worker: this.workerId,
        started_at: new Date().toISOString()
      })
      .eq('id', job.id)
      .eq('status', 'pending'); // Säkerställ att ingen annan tog jobbet

    if (updateError) {
      console.log(`[Worker ${this.workerId}] ⚠️ Optimistic update failed (race condition), job taken by another worker`);
      return null;
    }

    console.log(`[Worker ${this.workerId}] ✅ Successfully claimed job: ${job.job_id}`);
    return job as WorkerJob;
  }

  /**
   * Synkar worker_queue för en serverside-reserverad batch (för diagnostik)
   */
  private async syncQueueForReservedBatch(batch: any): Promise<void> {
    const jobId = batch.batch_id;
    console.log(`[Worker ${this.workerId}] 🔄 syncQueueForReservedBatch: ${jobId}`);
    
    try {
      // Försök uppdatera befintlig rad
      const { error: updateError, count } = await supabase
        .from('worker_queue')
        .update({
          status: 'processing',
          assigned_worker: this.workerId,
          started_at: new Date().toISOString()
        })
        .eq('job_id', jobId)
        .eq('status', 'pending');

      if (updateError) {
        console.warn(`[Worker ${this.workerId}] ⚠️ Could not update worker_queue for ${jobId}:`, updateError.message);
      } else if (count === 0) {
        // Ingen rad hittades (kanske RLS eller lag), gör graceful insert
        console.log(`[Worker ${this.workerId}] 📝 No existing queue entry, inserting for diagnostics...`);
        await supabase
          .from('worker_queue')
          .insert({
            queue_name: 'batch_analysis',
            job_type: 'batch',
            job_id: jobId,
            payload: {
              revision_id: batch.revision_id,
              module: batch.module,
              batch_id: jobId
            },
            status: 'processing',
            assigned_worker: this.workerId,
            started_at: new Date().toISOString()
          });
      } else {
        console.log(`[Worker ${this.workerId}] ✅ Updated ${count} worker_queue row(s) for ${jobId}`);
      }
    } catch (syncError) {
      console.warn(`[Worker ${this.workerId}] ⚠️ syncQueueForReservedBatch error:`, syncError);
    }
  }

  private async processJobs(): Promise<void> {
    console.log(`🔄 ${this.workerId} börjar processa jobb...`);
    
    while (this.isRunning) {
      try {
        // Kontrollera om vi kan ta fler jobb
        if (this.currentJobs >= this.maxConcurrentJobs) {
          await this.sleep(100);
          continue;
        }

        // Hämta nästa jobb
        const job = await this.getNextJob();
        
        if (!job) {
          // Inga jobb tillgängliga, vänta
          if (this.verboseLogging) {
            console.log(`⏳ ${this.workerId} väntar på jobb...`);
          }
          await this.sleep(this.batchSleepMs); // Använd batchSleepMs (900ms)
          continue;
        }

        console.log(`📥 ${this.workerId} fick jobb: ${job.job_id}`);
        console.log(`📥 ${this.workerId} fick jobb: ${job.job_id}`);
        this.stats.currentJob = job.job_id;
        this.stats.lastJobAt = new Date();

        // Markera revision som processing om den är i batching
        const revisionId = job?.payload?.revision_id;
        if (revisionId) {
          await supabase
            .from('revisions')
            .update({ status: 'processing', updated_at: new Date().toISOString() })
            .eq('id', revisionId)
            .neq('status', 'processing');
        }

        // Markera batch som processing för att visa progress direkt
        const batchId = job?.payload?.batch_id;
        if (batchId) {
          await supabase
            .from('batches')
            .update({ state: 'processing', started_at: new Date().toISOString(), updated_at: new Date().toISOString() })
            .eq('batch_id', batchId)
            .neq('state', 'completed');
        }

        // Processa jobb asynkront
        this.processJobAsync(job);
        
        // Vänta batchSleepMs innan nästa batch (leaky bucket)
        await this.sleep(this.batchSleepMs);

      } catch (error) {
        console.error(`❌ Fel i huvudloopen för ${this.workerId}:`, error);
        this.stats.errors++;
        await this.sleep(5000); // Längre väntetid vid fel
      }
    }

    console.log(`🛑 MiniAnalysisWorker ${this.workerId} main loop avslutat`);
  }

  private async createFallbackJob(batch: any): Promise<WorkerJob> {
    console.log(`🔄 Skapar fallback-jobb för batch ${batch.batch_id}`);
    
    // DEBUG: Logga batch-objektet för att se vad som kommer från edge function
    console.log(`🔍 Fallback batch object:`, {
      batch_id: batch.batch_id,
      has_data_sample: !!batch.data_sample,
      data_sample_type: typeof batch.data_sample,
      data_sample_length: Array.isArray(batch.data_sample) ? batch.data_sample.length : 'not array',
      data_sample_preview: batch.data_sample?.slice(0, 2)
    });

    // Försök hämta senaste fältmappning för denna modul för nuvarande användare
    let resolvedMapping: Record<string, string> | null = null;
    try {
      const { data: user } = await supabase.auth.getUser();
      const userId = user?.user?.id;
      if (userId) {
        const { data: mappingRow } = await supabase
          .from('field_mappings')
          .select('mapping')
          .eq('user_id', userId)
          .eq('module_name', batch.module || 'skattekonto')
          .order('updated_at', { ascending: false })
          .limit(1)
          .maybeSingle();
        resolvedMapping = (mappingRow as any)?.mapping || null;
      }
    } catch (e) {
      console.warn('⚠️ Kunde inte hämta fallback fältmappning:', e);
    }
    
    const fallbackJob: WorkerJob = {
      id: `fallback:${batch.id}`,
      job_id: `${batch.batch_id}-${Date.now()}`,
      queue_name: 'batch_analysis',
      job_type: 'batch',
      payload: {
        batch_id: batch.batch_id,
        revision_id: batch.revision_id,
        module: batch.module,
        data: batch.data_sample,
        field_mapping: resolvedMapping || null
      },
      status: 'processing',
      priority: 1,
      retry_count: batch.attempts || 0,
      max_retries: batch.max_attempts || 5,
      assigned_worker: this.workerId,
      started_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
      created_at: new Date().toISOString(),
    } as any;

    console.log(`🛠️ Fallback-jobb reserverat: ${fallbackJob.job_id} med ${fallbackJob.payload.data?.length || 0} rader${resolvedMapping ? ' (med fältmappning)' : ''}`);
    return fallbackJob;
  }

  private async processJobAsync(job: WorkerJob): Promise<void> {
    this.currentJobs++;
    
    try {
      await this.processJob(job);
    } catch (error) {
      console.error(`Fel vid processning av jobb ${job.job_id}:`, error);
    } finally {
      this.currentJobs--;
    }
  }

  private async processJob(job: WorkerJob): Promise<void> {
    try {
      switch (job.job_type) {
        case 'batch': {
          const processingResult = await this.processBatchJob(job.job_id, job.payload.data, job);
          await this.saveBatchResultWithTokens(job.payload.batch_id, processingResult);
          // Uppdatera revision totals
          await this.updateRevisionTotals(job.payload.revision_id, processingResult.tokensIn, processingResult.tokensOut);
          break;
        }
        case 'aggregate':
          await this.processAggregation(job.payload.revision_id, job.payload.module);
          break;
        case 'report':
          await this.reportWorker.generateFinalReport(job.payload.revision_id);
          break;
        default:
          throw new Error(`Okänd job-typ: ${job.job_type}`);
      }

      // Markera jobb som slutfört och rensa felmeddelanden (om från worker_queue)
      if (!job.id.startsWith('fallback:')) {
        await supabase
          .from('worker_queue')
          .update({
            status: 'completed',
            completed_at: new Date().toISOString(),
            last_error: null,
            retry_after: null
          })
          .eq('id', job.id);
      }

    } catch (error) {
      await this.handleJobError(job, error);
    }
  }

  private async handleJobError(job: WorkerJob, error: any): Promise<void> {
    const retryCount = (job.retry_count ?? 0) + 1;
    
    const errorMsg = (error?.message || String(error));
    const isRateLimit = /429|rate limit|too many requests/i.test(errorMsg);
    const isTimeout = /timeout|timed out/i.test(errorMsg);
    const isConnection = /failed to send|failed to fetch|functionsfetcherror|ECONN/i.test(errorMsg);

    // KRITISK FÖRÄNDRING: Rate-limit fel ger OBEGRÄNSADE retries (max 50), inte mot miniRetries
    const maxRetries = isRateLimit ? this.rateLimitRetries : this.miniRetries;

    if (retryCount <= maxRetries) {
      // Använd längre delays för rate limits, kortare för connection errors
      let baseDelayMs = this.retryDelays[Math.min(retryCount - 1, this.retryDelays.length - 1)];
      
      // Extra lång delay för rate limits (15 min max)
      if (isRateLimit) {
        baseDelayMs = Math.min(2 * 60000 * Math.pow(2, retryCount - 1), this.rateLimitMaxBackoffMs); // 2min, 4min, 8min, max 15min
        console.warn(`🚦 Rate limit detected - using extended backoff ${Math.round(baseDelayMs/1000)}s (försök ${retryCount}/${maxRetries})`);
      }
      
      const jitterMs = Math.random() * 2000; // 0-2s jitter
      const finalBackoffMs = Math.min(baseDelayMs + jitterMs, isRateLimit ? this.rateLimitMaxBackoffMs : 300000);
      
      const retryAfter = new Date(Date.now() + finalBackoffMs);
      
      console.warn(`⚠️ [${job.job_id}] RETRY ${retryCount}/${maxRetries} | type=${isRateLimit ? 'rate_limit' : isTimeout ? 'timeout' : isConnection ? 'connection' : 'other'} | backoff=${Math.round(finalBackoffMs/1000)}s | error=${errorMsg.slice(0, 200)}`);

      // Uppdatera jobb för retry
      if (!job.id.startsWith('fallback:')) {
        await supabase
          .from('worker_queue')
          .update({
            status: 'pending',
            retry_count: retryCount,
            retry_after: retryAfter.toISOString(),
            last_error: errorMsg.slice(0, 500)
          })
          .eq('id', job.id);
      }

      // Sätt batch-status baserat på error-typ
      if (job.payload?.batch_id) {
        const batchState = isRateLimit ? 'rate_limited' : 'retry';
        await supabase
          .from('batches')
          .update({
            state: batchState,
            last_error: errorMsg.slice(0, 500),
            retry_after: retryAfter.toISOString(),
            updated_at: new Date().toISOString()
          })
          .eq('batch_id', job.payload.batch_id);
      }
    } else {
      console.error(`❌ [${job.job_id}] DEAD | attempts=${maxRetries} | type=${isRateLimit ? 'RATE_LIMIT' : 'ERROR'} | error=${errorMsg.slice(0, 300)}`);
      console.log(`🔄 Graceful degradation: revision kommer fortsätta med andra batchar`);
      
      // Markera som permanent misslyckad (dead letter)
      if (!job.id.startsWith('fallback:')) {
        await supabase
          .from('worker_queue')
          .update({
            status: 'failed',
            last_error: `Max retries (${maxRetries}) exceeded: ${errorMsg.slice(0, 400)}`
          })
          .eq('id', job.id);
      }

      // Markera batch som misslyckad MEN trigga inte revision failure
      await supabase
        .from('batches')
        .update({
          state: 'failed',
          last_error: `Dead letter after ${maxRetries} attempts: ${errorMsg.slice(0, 400)}`,
          updated_at: new Date().toISOString()
        })
        .eq('batch_id', job.payload.batch_id);

      // Graceful degradation: Trigga aggregering ändå om övriga batchar är klara
      const [revisionId, module] = await this.getRevisionAndModuleForBatch(job.payload.batch_id);
      if (revisionId && module) {
        console.log(`🔄 [${job.job_id}] Kontrollerar om övriga batchar är klara för aggregering...`);
        await this.maybeTriggerAggregation(revisionId, module);
      }
    }

    this.stats.errors++;
  }

  private async registerWorker(): Promise<void> {
    const { error } = await supabase
      .from('workers')
      .upsert({
        id: this.workerId,
        status: 'active',
        last_heartbeat: new Date().toISOString(),
        jobs_processed: 0
      });

    if (error) {
      console.error(`Kunde inte registrera worker ${this.workerId}:`, error);
    }
  }

  private async completeJob(jobId: string): Promise<void> {
    const { error } = await supabase
      .from('worker_queue')
      .update({
        status: 'completed',
        completed_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      })
      .eq('id', jobId);

    if (error) {
      console.error(`Kunde inte markera jobb ${jobId} som slutfört:`, error);
    }
  }

  private validateBatchResult(result: any, jobId: string): BatchResult {
    if (!result || typeof result !== 'object') {
      throw new Error(`Ogiltigt batch-resultat för ${jobId}: måste vara objekt`);
    }

    // Validera required fields
    if (!result.batch_id || typeof result.batch_id !== 'string') {
      console.warn(`Saknad eller ogiltig batch_id för ${jobId}, använder jobId som fallback`);
      result.batch_id = jobId;
    }

    if (!result.metrics || typeof result.metrics !== 'object') {
      console.warn(`Saknad metrics för ${jobId}, använder standardvärden`);
      result.metrics = { sum_in: 0, sum_out: 0, count: 0 };
    }

    if (!Array.isArray(result.flags)) {
      console.warn(`Saknade eller ogiltiga flags för ${jobId}, använder tom array`);
      result.flags = [];
    }

    // Normalisera och validera
    const validated: BatchResult = {
      batch_id: result.batch_id,
      metrics: {
        sum_in: Number(result.metrics.sum_in) || 0,
        sum_out: Number(result.metrics.sum_out) || 0,
        count: Number(result.metrics.count) || 0
      },
      flags: Array.isArray(result.flags) 
        ? result.flags.map((flag: any, index: number) => ({
            id: flag.id || `flag_${jobId}_${index}`,
            rule: flag.rule || 'unknown_rule',
            score: Number(flag.score) || 0,
            severity: ['low', 'medium', 'high', 'critical'].includes(flag.severity) 
              ? flag.severity 
              : 'medium',
            description: flag.description || 'Ingen beskrivning'
          }))
        : [],
      top_counterparties: Array.isArray(result.top_counterparties) 
        ? result.top_counterparties.slice(0, 10) 
        : [],
      needs_human_review: Boolean(result.needs_human_review)
    };

    return validated;
  }

  private async saveBatchResult(batchId: string, result: BatchResult): Promise<void> {
    const { error } = await supabase
      .from('batches')
      .update({
        result_json: result as any,
        state: 'completed',
        completed_at: new Date().toISOString()
      })
      .eq('batch_id', batchId);

    if (error) {
      throw new Error(`Kunde inte spara batch-resultat: ${error.message}`);
    }

    // Försök trigga aggregering när alla batchar i modulen är klara
    const [revisionId, module] = await this.getRevisionAndModuleForBatch(batchId);
    if (revisionId && module) {
      await this.maybeTriggerAggregation(revisionId, module);
    }
  }

  private async saveBatchResultWithTokens(batchId: string, processingResult: BatchProcessingResult): Promise<void> {
    const { error } = await supabase
      .from('batches')
      .update({
        result_json: processingResult.batchResult as any,
        tokens_in: processingResult.tokensIn,
        tokens_out: processingResult.tokensOut,
        state: 'completed',
        completed_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        last_error: null,
        retry_after: null
      })
      .eq('batch_id', batchId);

    if (error) {
      throw new Error(`Failed to save batch result: ${error.message}`);
    }
    
    console.log(`💾 Batch ${batchId} sparad med ${processingResult.tokensIn + processingResult.tokensOut} tokens`);

    // Försök trigga aggregering när alla batchar i modulen är klara
    const [revisionId, module] = await this.getRevisionAndModuleForBatch(batchId);
    if (revisionId && module) {
      await this.maybeTriggerAggregation(revisionId, module);
    }
  }

  private async updateRevisionTotals(revisionId: string, tokensIn: number, tokensOut: number): Promise<void> {
    try {
      // Hämta aktuella totaler
      const { data: revision, error: fetchError } = await supabase
        .from('revisions')
        .select('total_tokens_in, total_tokens_out')
        .eq('id', revisionId)
        .single();

      if (fetchError) {
        console.error('Failed to fetch revision totals:', fetchError);
        return;
      }

      // Uppdatera med nya tokens
      const newTotalIn = (revision.total_tokens_in || 0) + tokensIn;
      const newTotalOut = (revision.total_tokens_out || 0) + tokensOut;
      const totalTokens = newTotalIn + newTotalOut;

      // Beräkna kostnad: 0.0036 SEK per 1000 tokens (Azure OpenAI mini-modell)
      const sekPer1kTokens = 0.0036;
      const totalCostSek = (totalTokens / 1000) * sekPer1kTokens;

      const { error: updateError } = await supabase
        .from('revisions')
        .update({
          total_tokens_in: newTotalIn,
          total_tokens_out: newTotalOut,
          total_cost_sek: totalCostSek,
          updated_at: new Date().toISOString()
        })
        .eq('id', revisionId);

      if (updateError) {
        console.error('Failed to update revision totals:', updateError);
      } else {
        console.log(`📊 Revision ${revisionId} totals: in=${newTotalIn}, out=${newTotalOut}, cost=${totalCostSek.toFixed(2)} SEK`);
      }
    } catch (error) {
      console.error('Error updating revision totals:', error);
    }
  }

  private async processAggregation(revisionId: string, module: string): Promise<void> {
    // Hämta alla kompletta batchar för modulen
    const { data: batches, error } = await supabase
      .from('batches')
      .select('result_json')
      .eq('revision_id', revisionId)
      .eq('module', module)
      .eq('state', 'completed');

    if (error) throw error;

    if (!batches || batches.length === 0) {
      throw new Error('Inga kompletta batchar att aggregera');
    }

    // Aggregera resultat
    const aggregated = this.aggregateBatchResults(batches.map(b => b.result_json as unknown as BatchResult));

    // Spara aggregerat resultat - kolla först om det redan finns
    const { data: existing } = await supabase
      .from('aggregates')
      .select('id')
      .eq('revision_id', revisionId)
      .eq('module', module)
      .single();

    if (existing) {
      console.log(`✅ Aggregat redan existerar för ${module}, uppdaterar...`);
      const { error: updateError } = await supabase
        .from('aggregates')
        .update({
          summary_json: aggregated,
          risk_score: this.calculateRiskScore(aggregated),
          flag_count: aggregated.total_flags,
          high_risk_flags: aggregated.high_risk_flags,
          updated_at: new Date().toISOString()
        })
        .eq('revision_id', revisionId)
        .eq('module', module);
      
      if (updateError) {
        console.error(`❌ Fel vid uppdatering av aggregat för ${module}:`, updateError);
        throw updateError;
      }
    } else {
      console.log(`🔄 Skapar nytt aggregat för ${module}...`);
      const { error: insertError } = await supabase
        .from('aggregates')
        .insert({
          revision_id: revisionId,
          module: module,
          summary_json: aggregated,
          risk_score: this.calculateRiskScore(aggregated),
          flag_count: aggregated.total_flags,
          high_risk_flags: aggregated.high_risk_flags
        });
      
      if (insertError) {
        console.error(`❌ Fel vid skapande av aggregat för ${module}:`, insertError);
        throw insertError;
      }
    }

    console.log(`✅ Aggregering klar för ${module}: ${aggregated.total_transactions} transaktioner, ${aggregated.total_flags} fynd`);

    // Om alla batchar i revisionen är klara (alla moduler), trigga slutrapport automatiskt
    try {
      const { data: allBatches2 } = await supabase
        .from('batches')
        .select('state')
        .eq('revision_id', revisionId);
      const pending = allBatches2?.filter(b => b.state !== 'completed' && b.state !== 'failed');
      if (!pending || pending.length === 0) {
        await supabase
          .from('worker_queue')
          .insert({
            queue_name: 'premium_report',
            job_id: `${revisionId}_final_report_${Date.now()}`,
            job_type: 'report',
            payload: { revision_id: revisionId },
            priority: 3
          });
        console.log(`🧾 Triggade slutrapport för revision ${revisionId}`);
      }
    } catch (e) {
      console.warn('⚠️ Kunde inte utvärdera/trigger rapport efter aggregering:', e);
    }
  }

  private aggregateBatchResults(batchResults: BatchResult[]): any {
    const aggregated = {
      total_transactions: 0,
      total_amount_in: 0,
      total_amount_out: 0,
      total_flags: 0,
      high_risk_flags: 0,
      critical_flags: 0,
      severity_distribution: { low: 0, medium: 0, high: 0, critical: 0 },
      unique_counterparties: new Set<string>(),
      flagged_rules: new Set<string>(),
      all_flags: [] as any[]
    };

    const cpCounts = new Map<string, number>();

    for (const batch of batchResults) {
      // Aggregera metrics (säkerställ numeriska värden)
      aggregated.total_transactions += Number(batch.metrics.count || 0);
      aggregated.total_amount_in += Number(batch.metrics.sum_in || 0);
      aggregated.total_amount_out += Number(batch.metrics.sum_out || 0);

      // Aggregera flags
      for (const flag of batch.flags) {
        aggregated.total_flags++;
        aggregated.severity_distribution[flag.severity as 'low'|'medium'|'high'|'critical']++;
        if (flag.severity === 'high' || flag.severity === 'critical') aggregated.high_risk_flags++;
        if (flag.severity === 'critical') aggregated.critical_flags++;
        aggregated.flagged_rules.add(flag.rule);
        aggregated.all_flags.push(flag);
      }

      // Samla motparter och räkna förekomst
      (batch.top_counterparties || []).forEach((cp: any) => {
        const name = typeof cp === 'string' ? cp : (cp?.name ?? String(cp));
        aggregated.unique_counterparties.add(name);
        cpCounts.set(name, (cpCounts.get(name) || 0) + 1);
      });
    }

    const top_counterparties = Array.from(cpCounts.entries())
      .map(([name, count]) => ({ name, count }))
      .sort((a, b) => b.count - a.count);

    // Om inga eller för få flags hittats totalt, lägg till informativa låg-risk-fynd
    try {
      if (aggregated.total_flags < 3) {
        const need = 3 - aggregated.total_flags;
        for (let i = 0; i < need; i++) {
          aggregated.all_flags.push({
            id: `aggregate_info_${Date.now()}_${i}`,
            rule: 'Informativt: kontrollspår',
            score: 1,
            severity: 'low',
            description: 'Inga tydliga avvikelser identifierade – kontrollspår (låg risk)'
          } as any);
          aggregated.total_flags++;
          aggregated.severity_distribution.low++;
        }
      }
    } catch (e) {
      console.warn('⚠️ Kunde inte lägga till informativa låg-risk-fynd på aggregatnivå:', e);
    }

    return {
      ...aggregated,
      unique_counterparties: Array.from(aggregated.unique_counterparties),
      top_counterparties,
      flagged_rules: Array.from(aggregated.flagged_rules),
      analysis_timestamp: new Date().toISOString(),
      batches_processed: batchResults.length
    };
  }

  private calculateRiskScore(aggregated: any): number {
    const weights = { critical: 4, high: 3, medium: 2, low: 1 };
    
    const totalWeightedScore = 
      (aggregated.severity_distribution?.critical || 0) * weights.critical +
      (aggregated.severity_distribution?.high || 0) * weights.high +
      (aggregated.severity_distribution?.medium || 0) * weights.medium +
      (aggregated.severity_distribution?.low || 0) * weights.low;

    const totalTxn = Number(aggregated.total_transactions) || 1; // Förhindra division med 0
    const score = Math.round((totalWeightedScore / totalTxn) * 10);
    
    // Garantera alltid ett giltigt tal mellan 0-100
    return Math.min(100, Math.max(0, isNaN(score) ? 0 : score));
  }

  private async maybeTriggerAggregation(revisionId: string, module: string): Promise<void> {
    try {
      const { data: batches } = await supabase
        .from('batches')
        .select('state')
        .eq('revision_id', revisionId)
        .eq('module', module);

      if (!batches) return;

      const completedCount = batches.filter(b => b.state === 'completed').length;
      const failedCount = batches.filter(b => b.state === 'failed').length;
      const totalCount = batches.length;

      // Kontrollera om alla batchar är antingen completed eller failed
      const allDone = batches.every(b => b.state === 'completed' || b.state === 'failed');
      const hasCompleted = completedCount > 0;

      // Om alla är klara (completed eller failed)
      if (allDone) {
        // Om ALLA batchar failade och INGA lyckades, markera revision som failed
        if (failedCount === totalCount && completedCount === 0) {
          console.error(`❌ Alla ${totalCount} batchar för ${module} misslyckades permanent - markerar revision som failed`);
          await supabase
            .from('revisions')
            .update({ 
              status: 'failed', 
              last_error: 'All batches failed permanently',
              updated_at: new Date().toISOString()
            })
            .eq('id', revisionId);
          return;
        }
        
        // Om minst en batch lyckades, kör aggregering
        if (hasCompleted) {
          const successRate = Math.round((completedCount / totalCount) * 100);
          console.log(`🎯 Alla batchar för ${module} är klara (${completedCount}/${totalCount} lyckades, ${failedCount} misslyckades, ${successRate}% success rate)`);
          console.log(`📊 Triggar aggregering med tillgänglig data för revision ${revisionId}`);
        
        // Kontrollera om aggregering redan finns
        const { data: existingAgg } = await supabase
          .from('aggregates')
          .select('id')
          .eq('revision_id', revisionId)
          .eq('module', module)
          .single();

        if (!existingAgg) {
          // Skapa partial aggregate om vissa batchar misslyckades
          if (failedCount > 0) {
            console.warn(`⚠️ Partial aggregering: ${failedCount} batchar misslyckades men fortsätter med ${completedCount} completed batchar`);
          }
          
          // Lägg till aggregerings-jobb i kön
          await supabase
            .from('worker_queue')
            .insert({
              queue_name: 'batch_analysis',
              job_id: `aggregate_${revisionId}_${module}_${Date.now()}`,
              job_type: 'aggregate',
              payload: { 
                revision_id: revisionId, 
                module: module,
                partial: failedCount > 0,
                success_rate: successRate
              },
              status: 'pending',
              priority: 10 // Hög prioritet för aggregering
            });
        }
      }
      }
    } catch (error) {
      console.error('Fel vid kontroll av aggregering:', error);
    }
  }

  private async getRevisionAndModuleForBatch(batchId: string): Promise<[string | null, string | null]> {
    const { data: batch } = await supabase
      .from('batches')
      .select('revision_id, module')
      .eq('batch_id', batchId)
      .single();

    return batch ? [batch.revision_id, batch.module] : [null, null];
  }

  /**
   * Validerar och saniterar fieldMapping
   * Säkerställer att kritiska fält finns och att Saldo inte används som Datum/Belopp
   */
  private validateFieldMapping(fieldMapping: Record<string, string> | null, jobId: string): Record<string, string> | null {
    if (!fieldMapping) {
      console.warn(`⚠️ [${jobId}] Ingen fieldMapping tillgänglig - använder heuristisk mappning`);
      return null;
    }

    // Kontrollera att kritiska fält finns
    const hasDate = !!fieldMapping['Datum'];
    const hasAmount = !!fieldMapping['Belopp'];
    const hasEvent = !!fieldMapping['Händelse'];

    if (!hasDate || !hasAmount) {
      console.error(`❌ [${jobId}] OFULLSTÄNDIG fieldMapping:`, {
        hasDate,
        hasAmount,
        hasEvent,
        fieldMapping
      });
      return null;
    }

    // KRITISK VALIDERING: Förhindra att Saldo används som Datum eller Belopp
    const saldoHeader = fieldMapping['Saldo'];
    const datumHeader = fieldMapping['Datum'];
    const beloppHeader = fieldMapping['Belopp'];

    if (saldoHeader && datumHeader && saldoHeader === datumHeader) {
      console.error(`❌ [${jobId}] KRITISKT FEL: Saldo är mappat som Datum! fieldMapping:`, fieldMapping);
      throw new Error('Ogiltigt fältmappning: Saldo kan inte användas som Datum');
    }

    if (saldoHeader && beloppHeader && saldoHeader === beloppHeader) {
      console.error(`❌ [${jobId}] KRITISKT FEL: Saldo är mappat som Belopp! fieldMapping:`, fieldMapping);
      throw new Error('Ogiltigt fältmappning: Saldo kan inte användas som Belopp');
    }

    console.log(`✅ [${jobId}] fieldMapping validerad:`, {
      Datum: datumHeader,
      Belopp: beloppHeader,
      Händelse: fieldMapping['Händelse'] || 'saknas',
      Saldo: saldoHeader || 'saknas'
    });

    return fieldMapping;
  }

  /**
   * Läser deployment-konfiguration från ai_deployments
   * Uppdaterar targetTPM baserat på max_tokens_per_minute
   */
  /**
   * Återställer orphaned jobs (batches stuck i processing > 10 min)
   * Körs vid worker-start för att städa upp efter eventuella crasher
   */
  private async cleanupOrphanedJobs(): Promise<void> {
    const tenMinutesAgo = new Date(Date.now() - 10 * 60 * 1000).toISOString();
    
    try {
      const { data: orphaned, error } = await supabase
        .from('batches')
        .select('id, state, updated_at, revision_id')
        .eq('state', 'processing')
        .lt('updated_at', tenMinutesAgo);
      
      if (error) {
        console.warn(`⚠️ [Worker ${this.workerId}] Could not check for orphaned jobs:`, error);
        return;
      }
      
      if (orphaned && orphaned.length > 0) {
        console.warn(`🧹 [Worker ${this.workerId}] Found ${orphaned.length} orphaned batches, resetting to pending...`);
        
        for (const batch of orphaned) {
          const { error: updateError } = await supabase
            .from('batches')
            .update({ 
              state: 'pending',
              last_error: 'Reset due to worker timeout (orphaned job cleanup)',
              updated_at: new Date().toISOString()
            })
            .eq('id', batch.id);
          
          if (updateError) {
            console.error(`❌ [Worker ${this.workerId}] Failed to reset batch ${batch.id}:`, updateError);
          } else {
            console.log(`✅ [Worker ${this.workerId}] Reset orphaned batch ${batch.id} to pending`);
          }
        }
      } else {
        console.log(`✅ [Worker ${this.workerId}] No orphaned jobs found`);
      }
    } catch (err) {
      console.warn(`⚠️ [Worker ${this.workerId}] Orphaned job cleanup failed:`, err);
    }
  }

  private async loadDeploymentConfig(): Promise<void> {
    try {
      const { data: deployment } = await supabase
        .from('ai_deployments')
        .select('max_tokens_per_minute, max_concurrent_requests')
        .eq('name', this.deploymentName)
        .eq('is_active', true)
        .maybeSingle();

      if (deployment) {
        // Sätt targetTPM till 60% av max för säkerhetsmarginal
        this.targetTPM = Math.floor((deployment.max_tokens_per_minute || 100000) * 0.6);
        console.log(`✅ [Worker ${this.workerId}] Laddade deployment config: TPM=${deployment.max_tokens_per_minute}, target=${this.targetTPM}, max_concurrent=${deployment.max_concurrent_requests}`);
      } else {
        console.warn(`⚠️ [Worker ${this.workerId}] Deployment ${this.deploymentName} hittades ej, använder fallback TPM=${this.targetTPM}`);
      }
    } catch (error) {
      console.error(`❌ [Worker ${this.workerId}] Kunde inte läsa deployment config:`, error);
    }
  }

  /**
   * Skapar systemprompt för analys med statistik-berikad kontext
   */
  private createAnalysisPromptWithStatistics(
    companyConfig: any,
    fieldMapping: any,
    statistics: StatisticalSummary,
    dataYear?: number
  ): string {
    const basePrompt = this.createAnalysisPrompt(companyConfig, fieldMapping, dataYear);
    
    // Bygg statistik-sektion med EXPLICIT ANALYSPERIOD FÖRST
    const statsLines: string[] = [
      '\n\n⚠️ KRITISK INSTRUKTION: ANALYSERA ENDAST DEN FAKTISKA DATAPERIODEN',
      `- Data börjar: ${statistics.analysis_period.start_date}`,
      `- Data slutar: ${statistics.analysis_period.end_date}`,
      `- Total tidsperiod: ${statistics.analysis_period.start_date.substring(0, 7)} till ${statistics.analysis_period.end_date.substring(0, 7)}`,
      `- VIKTIGT: Använd DENNA period för alla förfallodagsberäkningar`,
      `- IGNORERA alla referenser till andra år i skattekalendern - den är en MALL`,
      '',
      '📊 PRE-BERÄKNAD STATISTIK (analysis_context.statistics):',
      'Du har tillgång till färdigberäknad statistik per skattetyp. Använd dessa värden i dina regler.'
    ];
    
    if (statistics.tax_types.length > 0) {
      statsLines.push('\nSTATISTIK PER SKATTETYP:');
      for (const taxType of statistics.tax_types) {
        statsLines.push(
          `- ${taxType.tax_type}: ${taxType.count} transaktioner, ` +
          `median=${taxType.monthly_median} SEK, ` +
          `genomsnitt=${taxType.monthly_average} SEK, ` +
          `std_dev=${taxType.std_deviation}, ` +
          `CV=${taxType.coefficient_of_variation} ` +
          `(min=${taxType.min}, max=${taxType.max})`
        );
      }
    }
    
    if (statistics.payment_behavior.total_payments > 0) {
      const pb = statistics.payment_behavior;
      statsLines.push(
        `\nBETALNINGSBETEENDE:`,
        `- Totalt ${pb.total_payments} betalningar (${pb.late_payments} sena, ${pb.on_time_payments} i tid)`,
        `- Genomsnittlig försening: ${pb.average_days_late} dagar (max: ${pb.max_days_late} dagar)`,
        `- Räntor: ${pb.interest_charges_count} st, Avgifter: ${pb.fee_charges_count} st`
      );
    }
    
    statsLines.push(
      '\n⚠️ VIKTIGT: Använd statistics-objektet för att beräkna avvikelser:',
      '- För regel 3 (Stora beloppsavvikelser): Jämför transaction.amount mot statistics.tax_types[X].monthly_median',
      '- För regel 4 (Oregelbundna mönster): Använd statistics.tax_types[X].coefficient_of_variation > 0.15 som tröskel',
      '- För regel 1 (Sena betalningar): Använd statistics.payment_behavior för kontext',
      '\nDu behöver INTE beräkna median, genomsnitt eller standardavvikelse själv – allt finns i statistics.'
    );
    
    return basePrompt + '\n' + statsLines.join('\n');
  }

  private createAnalysisPrompt(
    companyConfig?: { companySize: 'small' | 'large'; vatReportingPeriod: 'monthly' | 'quarterly' },
    fieldMapping?: Record<string, string> | null,
    dataYear?: number
  ): string {
    const basePrompt = createTaxAnalysisPrompt(companyConfig || DEFAULT_COMPANY_CONFIG, dataYear);
    
    // STEG 4: Inkludera explicit fältmappning i prompten
    if (fieldMapping) {
      const mappingSection = `
FÄLTMAPPNING (KRITISKT - använd ENDAST dessa kolumnnamn från originaldata):
${Object.entries(fieldMapping)
  .map(([target, source]) => `- ${target}: finns i kolumn "${source}"`)
  .join('\n')}

VIKTIGT: 
- Data har redan normaliserats till standardfält (amount, date, event) via denna mappning
- "Saldo" är INTE datum eller belopp - ignorera detta fält för datum/beloppsanalys
- Om du ser ogiltiga datumvärden (t.ex. stora tal som "-33 592"), rapportera detta som datakvalitetsfel
`;
      return mappingSection + '\n' + basePrompt;
    }
    
    return basePrompt;
  }

  /**
   * Förbereder analysdata med ROBUSTAS fältmappning och preflight-validering
   * STEG 1: Normalisera mapping (konvertera "Kolumn N" → headers[n-1])
   * STEG 2: Preflight-validering (coverage checks)
   * STEG 3: Fail-fast om coverage under tröskel
   * STEG 4: Använd headers-array för deterministisk upplösning
   */
  private prepareAnalysisData(
    batchData: any[], 
    fieldMapping?: Record<string, string> | null,
    headers?: string[] | null
  ): any {
    console.log(`🔍 [prepareAnalysisData] Called with ${batchData.length} rows`);
    console.log(`🔍 [prepareAnalysisData] fieldMapping:`, fieldMapping);
    console.log(`🔍 [prepareAnalysisData] headers:`, headers);
    
    if (!fieldMapping) {
      console.warn('⚠️ Ingen fieldMapping - använder heuristisk normalisering (kan ge fel resultat)');
    }

    if (batchData.length > 0) {
      console.log(`🔍 First row keys:`, Object.keys(batchData[0]));
      console.log(`🔍 First row sample:`, batchData[0]);
    }
    
    // STEG 1: Normalisera mapping - konvertera "Kolumn N" → index ELLER headers[n-1] endast om headers är rimliga
    const normalizedMapping: Record<string, string> = {};

    const headersLookLikeData = Array.isArray(headers)
      ? headers.filter(Boolean).filter(h => {
          const s = String(h).trim();
          // Heuristik: ser ut som datum eller rena siffror/belopp => troligen INTE riktiga rubriker
          const isDate = /^(\d{4}-\d{2}-\d{2}|\d{2}[\/.-]\d{2}[\/.-]\d{4}|\d{8})$/.test(s);
          const isNumericish = /^-?[\d\s\u00A0\u2009\u202F.,]+$/.test(s);
          return isDate || isNumericish;
        }).length > (headers?.length || 0) * 0.6 // >60% ser ut som data => behandla som saknade headers
      : false;

    if (fieldMapping) {
      for (const [targetField, sourceHeader] of Object.entries(fieldMapping)) {
        if (!sourceHeader) continue;
        const srcStr = String(sourceHeader);
        const kolumnMatch = /^Kolumn\s+(\d+)$/i.exec(srcStr);

        if (kolumnMatch) {
          // Om headers sannolikt är riktiga rubriker kan vi mappa via headers[n-1], annars behåll "Kolumn N"
          if (headers && !headersLookLikeData) {
            const idx = Math.max(0, Number(kolumnMatch[1]) - 1);
            if (idx < headers.length) {
              normalizedMapping[targetField] = headers[idx];
              console.log(`🔄 Converted "${sourceHeader}" → "${headers[idx]}" for ${targetField}`);
            } else {
              console.error(`❌ Invalid column index ${idx} for "${sourceHeader}" (headers length: ${headers.length})`);
            }
          } else {
            // Behåll "Kolumn N" för index-baserad lookup senare
            normalizedMapping[targetField] = srcStr;
          }
        } else {
          normalizedMapping[targetField] = srcStr;
        }
      }
    }
    
    const orderedKeys = Object.keys(batchData[0] || {});
    const saldoHeader = normalizedMapping['Saldo'] ? String(normalizedMapping['Saldo']) : undefined;
    
    // STEG 2: Normalisera transaktioner med DETERMINISTIC lookup
    let transactions = batchData.map((transaction, index) => {
      const normalized: any = {
        id: `txn_${index}`
      };

      if (Object.keys(normalizedMapping).length > 0) {
        // STEG 3: Strikt mappning med normaliserad mapping (inga "Kolumn N")
        if (index === 0) {
          console.log(`🔍 First txn mapping debug:`, {
            normalizedMapping,
            transactionKeys: Object.keys(transaction),
            transaction
          });
        }
        
        for (const [targetField, sourceHeader] of Object.entries(normalizedMapping)) {
          if (!sourceHeader) continue;

          // ROBUST DETERMINISTISK LOOKUP med fuzzy header matching
          let raw: any = (transaction as any)[sourceHeader as any];
          let resolvedKey = sourceHeader;

          // Fallback 1: "Kolumn N" → index-based lookup
          const kolumnMatch = /^Kolumn\s+(\d+)$/i.exec(String(sourceHeader));
          if ((raw === undefined || raw === null) && kolumnMatch) {
            const idx = Math.max(0, Number(kolumnMatch[1]) - 1);
            const keyAtIdx = orderedKeys[idx];
            if (keyAtIdx !== undefined) {
              raw = (transaction as any)[keyAtIdx];
              resolvedKey = keyAtIdx;
              if (index === 0) console.log(`🔄 [${targetField}] Resolved "${sourceHeader}" via index ${idx} → "${keyAtIdx}"`);
            }
          }

          // Fallback 2: Fuzzy header matching (NBSP, quotes, case-insensitive, diacritics)
          if (raw === undefined || raw === null) {
            const normalized = normalizeHeaderName(sourceHeader);
            const matchingKey = orderedKeys.find(k => normalizeHeaderName(k) === normalized);
            if (matchingKey) {
              raw = (transaction as any)[matchingKey];
              resolvedKey = matchingKey;
              if (index === 0) console.log(`🔄 [${targetField}] Resolved "${sourceHeader}" via fuzzy match → "${matchingKey}"`);
            }
          }

          if (raw === undefined || raw === null) {
            // Kritiskt fält saknas
            if (['Datum', 'Belopp', 'Händelse'].includes(targetField)) {
              if (index === 0) {
                console.error(`❌ [${targetField}] Kritiskt fält saknas i data:`, {
                  sourceHeader,
                  availableKeys: orderedKeys,
                  normalizedTarget: normalizeHeaderName(sourceHeader),
                  normalizedAvailable: orderedKeys.map(k => ({ key: k, normalized: normalizeHeaderName(k) }))
                });
              }
            }
            continue;
          }

          switch (targetField) {
            case 'Belopp': {
              const num = typeof raw === 'number' ? raw : normalizeSwedishNumber(String(raw));
              normalized.amount = num ?? null;
              if (typeof num === 'number') normalized.direction = num >= 0 ? 'in' : 'out';
              normalized.raw_amount = raw;
              break;
            }
            case 'Datum': {
              // STEG 5: Skydd mot Saldo som datum
              const isSaldoHeader = saldoHeader && sourceHeader && String(sourceHeader) === saldoHeader;
              if (isSaldoHeader) {
                if (index === 0) console.error('❌ KRITISKT: Datum är mappat till Saldo-kolumn - hoppar över');
                normalized.date = null;
                break;
              }
              const rawStr = raw !== undefined && raw !== null ? String(raw).trim() : '';
              const iso = rawStr ? normalizeSwedishDate(rawStr) : null;
              normalized.raw_date = raw;
              normalized.date = iso || null; // Sätt null om datum inte kan normaliseras
              if (!iso && index === 0) {
                console.warn('⚠️ Kunde inte normalisera datumvärde:', raw);
              }
              break;
            }
            case 'Händelse': {
              normalized.event = raw;
              break;
            }
            case 'Förfallodatum': {
              const iso = typeof raw === 'string' ? normalizeSwedishDate(raw) : null;
              normalized.due_date = iso || raw;
              break;
            }
            case 'TransaktionsID': {
              normalized.txn_id = raw;
              break;
            }
            case 'Saldo': {
              const num = typeof raw === 'number' ? raw : normalizeSwedishNumber(String(raw));
              normalized.balance = num ?? null;
              normalized.raw_balance = raw;
              break;
            }
            case 'DeklareratBelopp': {
              const num = typeof raw === 'number' ? raw : normalizeSwedishNumber(String(raw));
              normalized.declared_amount = num ?? null;
              normalized.raw_declared_amount = raw;
              break;
            }
            default: {
              normalized[targetField] = raw;
            }
          }
        }
        
        // Sätt explicit null för saknade kritiska fält
        if (normalized.amount === undefined) normalized.amount = null;
        if (normalized.date === undefined) normalized.date = null;
        if (normalized.event === undefined) normalized.event = null;
        
      } else {
        // Ingen mappning – heuristisk normalisering via rubriker (endast fallback)
        const keys = Object.keys(transaction || {});

        // Belopp
        const amountKey = keys.find((k) => /belopp|summa|amount|value|belopp_sek|amount_sek/i.test(k));
        const rawAmt: any = amountKey ? (transaction as any)[amountKey] : undefined;
        const numAmt = typeof rawAmt === 'number' ? rawAmt : normalizeSwedishNumber(String(rawAmt ?? ''));
        if (numAmt !== null && !isNaN(Number(numAmt))) {
          normalized.amount = Number(numAmt);
          normalized.direction = Number(numAmt) >= 0 ? 'in' : 'out';
        } else {
          normalized.amount = null;
        }
        normalized.raw_amount = rawAmt;

        // Datum
        const dateKey = keys.find((k) => /datum|date|posting_date|transaction_date/i.test(k));
        const rawDate: any = dateKey ? (transaction as any)[dateKey] : undefined;
        const iso = typeof rawDate === 'string' ? normalizeSwedishDate(rawDate) : null;
        normalized.raw_date = rawDate;
        normalized.date = iso || null; // Sätt null om datum inte kan normaliseras

        // Händelse/Text
        const evtKey = keys.find((k) => /händelse|handelse|event|typ|type|text|beskrivning|description|transaktion/i.test(k));
        if (evtKey) normalized.event = (transaction as any)[evtKey];

        // Referens/ID
        const refKey = keys.find((k) => /referens|reference|ref|id|ärendenummer|arendenummer|case_id|reference_id|löpnummer|lopnummer|transaction_id|transaktionsid/i.test(k));
        if (refKey) normalized.txn_id = (transaction as any)[refKey];
      }

      return normalized;
    });

    console.log(`✅ Normalized ${transactions.length} transactions. Sample of first 3:`, 
      transactions.slice(0, 3).map(t => ({
        id: t.id,
        date: t.date,
        amount: t.amount,
        event: t.event,
        raw_date: t.raw_date,
        raw_amount: t.raw_amount
      }))
    );

    // STEG 4: Preflight-validering - coverage checks
    const preflightSample = transactions.slice(0, Math.min(50, transactions.length));
    const dateCoverage = preflightSample.filter(t => t.date !== null && t.date !== undefined).length / preflightSample.length;
    const amountCoverage = preflightSample.filter(t => t.amount !== null && t.amount !== undefined).length / preflightSample.length;
    const eventCoverage = preflightSample.filter(t => t.event !== null && t.event !== undefined && String(t.event).trim() !== '').length / preflightSample.length;

    console.log(`📊 [Preflight Coverage] Date: ${Math.round(dateCoverage * 100)}%, Amount: ${Math.round(amountCoverage * 100)}%, Event: ${Math.round(eventCoverage * 100)}%`);

    // STEG 5: Fail-fast om coverage under tröskel – men försök först auto-schema som fallback
    const errors: string[] = [];
    if (dateCoverage < 0.7) errors.push(`Datum-täckning för låg: ${Math.round(dateCoverage * 100)}% (krav: 70%)`);
    if (amountCoverage < 0.9) errors.push(`Belopp-täckning för låg: ${Math.round(amountCoverage * 100)}% (krav: 90%)`);
    
    if (errors.length > 0) {
      console.warn(`⚠️ Preflight under tröskel. Försöker auto-infer schema... (${errors.join('; ')})`);

      // Bygg rad-arrayer i kolumnordning för sniffern
      const rowsArray: string[][] = batchData.map((transaction: any) =>
        orderedKeys.map(k => String((transaction as any)[k] ?? ''))
      );

      try {
        const sniff = inferSchema(rowsArray, Math.min(200, rowsArray.length));
        const autoMapping: Record<string, string> = {};
        if (sniff.schema.date_idx !== null) autoMapping['Datum'] = `Kolumn ${sniff.schema.date_idx + 1}`;
        if (sniff.schema.amount_idx !== null) autoMapping['Belopp'] = `Kolumn ${sniff.schema.amount_idx + 1}`;
        if (sniff.schema.event_idx !== null) autoMapping['Händelse'] = `Kolumn ${sniff.schema.event_idx + 1}`;
        if (sniff.schema.balance_idx !== null) autoMapping['Saldo'] = `Kolumn ${sniff.schema.balance_idx + 1}`;

        console.log('🧭 Auto-inferred mapping:', autoMapping, 'Warnings:', sniff.warnings);

        // Normalisera med auto-mapping (index-baserat)
        const autoTransactions = batchData.map((transaction: any, index: number) => {
          const normalized: any = { id: `txn_${index}` };

          for (const [targetField, src] of Object.entries(autoMapping)) {
            const m = /^Kolumn\s+(\d+)$/i.exec(String(src));
            if (!m) continue;
            const idx = Math.max(0, Number(m[1]) - 1);
            const keyAtIdx = orderedKeys[idx];
            let raw: any = keyAtIdx !== undefined ? (transaction as any)[keyAtIdx] : undefined;

            switch (targetField) {
              case 'Belopp': {
                const num = typeof raw === 'number' ? raw : normalizeSwedishNumber(String(raw));
                normalized.amount = num ?? null;
                if (typeof num === 'number') normalized.direction = num >= 0 ? 'in' : 'out';
                normalized.raw_amount = raw;
                break;
              }
              case 'Datum': {
                const rawStr = raw !== undefined && raw !== null ? String(raw).trim() : '';
                const iso = rawStr ? normalizeSwedishDate(rawStr) : null;
                normalized.raw_date = raw;
                normalized.date = iso || null;
                break;
              }
              case 'Händelse': {
                normalized.event = raw;
                break;
              }
              case 'Saldo': {
                const num = typeof raw === 'number' ? raw : normalizeSwedishNumber(String(raw));
                normalized.balance = num ?? null;
                normalized.raw_balance = raw;
                break;
              }
              default:
                normalized[targetField] = raw;
            }
          }

          if (normalized.amount === undefined) normalized.amount = null;
          if (normalized.date === undefined) normalized.date = null;
          if (normalized.event === undefined) normalized.event = null;
          return normalized;
        });

        const fallbackSample = autoTransactions.slice(0, Math.min(50, autoTransactions.length));
        const fDate = fallbackSample.filter(t => t.date).length / fallbackSample.length;
        const fAmt = fallbackSample.filter(t => t.amount !== null && t.amount !== undefined).length / fallbackSample.length;
        const fEvt = fallbackSample.filter(t => t.event && String(t.event).trim() !== '').length / fallbackSample.length;

        console.log(`📊 [Auto-Schema Coverage] Date: ${Math.round(fDate * 100)}%, Amount: ${Math.round(fAmt * 100)}%, Event: ${Math.round(fEvt * 100)}%`);

        if (fDate >= 0.7 && fAmt >= 0.9) {
          console.log('✅ Auto-schema passerade trösklar – använder auto-inferred mapping');
          transactions = autoTransactions; // ersätt med auto-normaliserade transaktioner
        } else {
          const errorMsg = `❌ PREFLIGHT FAILED: ${errors.join('; ')}. Auto-schema hjälpte ej (Date ${Math.round(fDate*100)}%, Amount ${Math.round(fAmt*100)}%). Felaktig fältmappning – kontrollera kolumnval eller ladda fil igen.`;
          console.error(errorMsg);
          throw new Error(errorMsg);
        }
      } catch (e) {
        const errorMsg = `❌ PREFLIGHT FAILED: ${errors.join('; ')}. Auto-schema kunde inte härleda tydligt schema.`;
        console.error(errorMsg, e);
        throw new Error(errorMsg);
      }
    }


    // Förberäkna totals som facit till modellen
    const totalsSums = transactions.reduce((acc: any, t: any) => {
      const a = typeof t.amount === 'number' ? t.amount : null;
      if (a !== null) {
        if (a >= 0) acc.sum_in += a; else acc.sum_out += Math.abs(a);
      }
      return acc;
    }, { sum_in: 0, sum_out: 0 });

    const totals = { ...totalsSums, count: transactions.length };

    return {
      transactions,
      analysis_context: {
        batch_size: batchData.length,
        timestamp: new Date().toISOString(),
        module: 'skattekonto_analysis',
        has_field_mapping: !!fieldMapping,
        field_mapping: fieldMapping || null,
        domain: 'skattekonto',
        assumptions: {
          single_counterparty: 'Skatteverket',
          counterparties_irrelevant: true,
          no_cash_transactions: true,
          amounts_normalized: true,
          dates_normalized: transactions.every((t: any) => !!t.date)
        },
        precomputed_totals: totals
      }
    };
  }

  private async callWithRetry<T>(operation: () => Promise<T>, maxAttempts: number = 5): Promise<T> {
    let lastError: Error | null = null;
    let realAttempts = 0; // Räknar endast riktiga fel
    let rateLimitAttempts = 0; // Separat räknare för 429
    
    while (realAttempts < maxAttempts) {
      try {
        return await operation();
      } catch (error) {
        lastError = error as Error;
        const errorMsg = (error as Error).message || String(error);
        
        // Klassificera fel för lämplig backoff
        const isRateLimit = /429|rate limit|too many requests|quota/i.test(errorMsg);
        const isTimeout = /timeout|timed out/i.test(errorMsg);
        const isConnection = /failed to send|failed to fetch|ECONN|temporarily/i.test(errorMsg);
        const retryable = isRateLimit || isTimeout || isConnection;
        
        if (!retryable) {
          console.error(`❌ ${this.workerId} Non-retryable error, aborting: ${errorMsg.slice(0, 200)}`);
          throw lastError;
        }

        if (isRateLimit) {
          rateLimitAttempts++;
          
          // Ge upp efter extremt många 429-försök (50+) för att undvika evig loop
          if (rateLimitAttempts >= this.rateLimitRetries) {
            console.error(`❌ Azure rate-limitad efter ${rateLimitAttempts} försök. Ger upp.`);
            throw lastError;
          }
          
          // Exponentiell backoff för 429, mycket längre än vanliga retries
          // Start: 2 min, sedan 4 min, 8 min, max 15 min
          const backoffBase = 2 * 60 * 1000; // 2 min
          const exponentialMs = backoffBase * Math.pow(2, Math.min(rateLimitAttempts - 1, 3));
          const jitter = Math.random() * 30000; // 0-30s jitter
          const waitMs = Math.min(exponentialMs + jitter, this.rateLimitMaxBackoffMs);
          
          console.warn(`⏱️ [429 Rate Limit] Försök ${rateLimitAttempts}/${this.rateLimitRetries}. Väntar ${Math.round(waitMs / 1000)}s...`);
          await this.sleep(waitMs);
          
          // OBS: öka INTE realAttempts - 429 räknas inte som ett "riktigt" fel
          continue;
        } else {
          // Riktigt fel (5xx, parsing, timeout, connection) - räkna mot max attempts
          realAttempts++;
          
          if (realAttempts >= maxAttempts) {
            break;
          }

          // Exponential backoff med jitter för riktiga fel
          const baseDelayMs = 500 * Math.pow(2, realAttempts - 1); // 500ms, 1s, 2s, 4s, 8s
          const waitMs = Math.min(baseDelayMs * (0.8 + Math.random() * 0.4), 8000); // jitter ±20%, max 8s per iteration
          
          console.warn(`⚠️ ${this.workerId} attempt ${realAttempts}/${maxAttempts} | type=${isTimeout ? 'timeout' : 'connection'} | retry in ${Math.round(waitMs)}ms | error: ${errorMsg.slice(0, 150)}`);
          await this.sleep(waitMs);
        }
      }
    }

    throw lastError || new Error('Operation failed after retries');
  }

  private sleep(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  private async getRandomDelay(): Promise<number> {
    // Random delay mellan 200-800ms för att undvika thundering herd
    return Math.random() * 600 + 200;
  }

  /**
   * Token-bucket throttling: håll koll på tokens senaste 60 sekunder
   */
  private recordTokenUsage(tokens: number): void {
    const now = Date.now();
    this.tokenWindow.push({ timestamp: now, tokens });
    // Rensa gamla poster (äldre än 60s)
    this.tokenWindow = this.tokenWindow.filter(entry => now - entry.timestamp < 60000);
  }

  private getRecentTokens(): number {
    const now = Date.now();
    return this.tokenWindow
      .filter(entry => now - entry.timestamp < 60000)
      .reduce((sum, entry) => sum + entry.tokens, 0);
  }

  private async throttleIfNeeded(estimatedTokens: number): Promise<void> {
    const now = Date.now();
    const oneMinuteAgo = now - 60000;

    // Ta bort gamla poster från windows
    this.tokenWindow = this.tokenWindow.filter(entry => entry.timestamp > oneMinuteAgo);
    this.requestWindow = this.requestWindow.filter(entry => entry.timestamp > oneMinuteAgo);

    // Beräkna current TPM och RPM
    const currentTPM = this.tokenWindow.reduce((sum, entry) => sum + entry.tokens, 0);
    const currentRPM = this.requestWindow.length;
    
    // Kontrollera RPM-gräns först (viktigare än TPM)
    if (currentRPM >= this.targetRPM) {
      const oldestRequest = this.requestWindow[0];
      if (oldestRequest) {
        const waitMs = Math.max(0, (oldestRequest.timestamp + 60000) - now + 2000); // +2s säkerhetsmarginal
        if (waitMs > 0) {
          console.log(`⏳ RPM-Throttling: väntar ${Math.round(waitMs/1000)}s (current=${currentRPM}/${this.targetRPM} req/min)`);
          await this.sleep(waitMs);
        }
      }
    }
    
    // Kontrollera TPM-gräns
    if (currentTPM + estimatedTokens > this.targetTPM) {
      const oldestEntry = this.tokenWindow[0];
      if (oldestEntry) {
        const waitMs = Math.max(0, (oldestEntry.timestamp + 60000) - now + 2000); // +2s säkerhetsmarginal
        if (waitMs > 0) {
          console.log(`⏳ TPM-Throttling: väntar ${Math.round(waitMs/1000)}s (current=${currentTPM}, target=${this.targetTPM}, est=${estimatedTokens})`);
          await this.sleep(waitMs);
        }
      }
    }

    // Registrera denna request
    this.requestWindow.push({ timestamp: now });
  }
}